import React, { useMemo, useState } from "react";
import LangLink from "../LangLink";
import { useTranslation } from "react-i18next";
import "./Footer.css";

import logo from "../../../assets/images/vucalogo.svg";
import orangeLogo from "../../../assets/images/logo.png";

import fb from "../../../assets/icons/social1.svg";
import ig from "../../../assets/icons/social2.svg";
import li from "../../../assets/icons/social3.svg";
import { usePageContent } from "../../../hooks/usePublicQueries";
import { subscribeEmail } from "../../../api/subscribe";
import { resolveMediaUrl } from "../../../utils/media";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const extractSubscribeError = (error) => {
  const data = error?.response?.data;
  if (data && typeof data === "object") {
    if (typeof data.message === "string" && data.message.trim()) return data.message;
    const errs = data.errors;
    if (errs && typeof errs === "object") {
      for (const key of ["Email", "email"]) {
        const list = errs[key];
        if (Array.isArray(list) && typeof list[0] === "string" && list[0].trim()) {
          return list[0];
        }
      }
      const firstList = Object.values(errs).find(
        (v) => Array.isArray(v) && typeof v[0] === "string" && v[0].trim()
      );
      if (firstList) return firstList[0];
    }
  }
  return "Subscription failed. Please try again.";
};

const QUICK_LINKS = [
  { labelKey: "footer.quickLinks.solutions", url: "/allsolutions" },
  { labelKey: "footer.quickLinks.products", url: "/products" },
  { labelKey: "footer.quickLinks.projects", url: "/allprojects" },
  { labelKey: "footer.quickLinks.services", url: "/services" },
  { labelKey: "footer.quickLinks.sectors", url: "/sectors/commercial" },
  { labelKey: "footer.quickLinks.estimator", url: "/estimator" },
  { labelKey: "footer.quickLinks.blog", url: "/blog" },
];

const COMPANY_LINKS = [
  { labelKey: "footer.companyLinks.aboutUs", url: "/about" },
  { labelKey: "footer.companyLinks.downloads", url: "/downloads" },
  { labelKey: "footer.companyLinks.academy", url: "/academy" },
  { labelKey: "footer.companyLinks.contactUs", url: "/contactus" },
  { labelKey: "footer.companyLinks.downloadProfile", url: "/downloads" },
  { labelKey: "footer.companyLinks.technicalAnnual", url: "/downloads" },
  { labelKey: "footer.companyLinks.careers", url: "/careers" },
];

const FALLBACK = {
  newsletterTitleLine1: "Stay up to date",
  newsletterTitleLine2: "with our newsletter",
  newsletterPlaceholder: "Enter your email",
  newsletterButtonText: "Subscribe",
  companyTextLine1: "Iraq's leading gypsum manufacturer.",
  companyTextLine2: "Building the future of construction.",
  locationText: "Baghdad & Erbil, Iraq",
  emailText: "info@irqgyp.com",
  websiteText: "irqgyp.com",
  facebookUrl: "https://www.facebook.com/irqgyp",
  instagramUrl: "https://www.instagram.com/irqgyp",
  linkedinUrl: "https://www.linkedin.com/company/irqgyp",
  copyrightText: "© {year} IRQGYP. All rights reserved.",
  privacyPolicyText: "Privacy Policy",
  privacyPolicyUrl: "/privacy-policy",
  termsText: "Terms of Use",
  termsUrl: "/terms",
  sitemapText: "Sitemap",
  sitemapUrl: "/sitemap",
  vucaUrl: "https://vuca.li/",
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const tryParseJsonObject = (value) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const unwrapSectionData = (section) => {
  if (section == null) return section;
  if (typeof section === "string") {
    const parsed = tryParseJsonObject(section);
    return parsed ? unwrapSectionData(parsed) : section;
  }
  if (typeof section !== "object") return section;
  if (section.data && typeof section.data === "object") return section.data;
  return section;
};

const findPageSection = (root, slug) => {
  if (!root || typeof root !== "object") return undefined;
  const target = normalizeSectionSlug(slug);
  const hit = Object.keys(root).find((k) => normalizeSectionSlug(k) === target);
  return hit != null ? root[hit] : undefined;
};

const coalesceFooterPayload = (section) => {
  const direct = unwrapSectionData(section);
  const payload = direct && typeof direct === "object" ? { ...direct } : {};
  const rawValue = section?.value ?? direct?.value;

  if (rawValue && typeof rawValue === "object") Object.assign(payload, rawValue);
  if (typeof rawValue === "string") {
    const parsed = tryParseJsonObject(rawValue);
    if (parsed) Object.assign(payload, parsed);
  }

  return payload;
};

const pickString = (obj, keys, fallback = "") => {
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return fallback;
};

const isExternalUrl = (url) => /^https?:\/\//i.test(String(url || "").trim());

const FooterNavLink = ({ to, className, children }) => {
  const href = String(to || "").trim();
  if (!href) return null;
  if (isExternalUrl(href)) {
    return (
      <a href={href} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }
  return (
    <LangLink to={href} className={className}>
      {children}
    </LangLink>
  );
};

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { data: footerContent } = usePageContent("shared", i18n.language);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const trimmed = email.trim();
    if (!trimmed) {
      setFeedback({ type: "error", message: "Please enter your email." });
      return;
    }
    if (!EMAIL_REGEX.test(trimmed)) {
      setFeedback({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }
    setSubmitting(true);
    setFeedback({ type: "", message: "" });
    try {
      await subscribeEmail({ email: trimmed });
      setEmail("");
      setFeedback({
        type: "success",
        message: "Thanks for subscribing! We'll be in touch.",
      });
    } catch (error) {
      setFeedback({ type: "error", message: extractSubscribeError(error) });
    } finally {
      setSubmitting(false);
    }
  };

  const view = useMemo(() => {
    const payload = coalesceFooterPayload(findPageSection(footerContent, "footer"));
    const year = new Date().getFullYear();
    const copyrightRaw = pickString(
      payload,
      ["copyrightText", "copyright", "bottomText"],
      FALLBACK.copyrightText
    );

    const companyLine1 = pickString(
      payload,
      ["companyTextLine1"],
      FALLBACK.companyTextLine1
    );
    const companyLine2 = pickString(
      payload,
      ["companyTextLine2"],
      FALLBACK.companyTextLine2
    );

    return {
      newsletterTitleLine1: pickString(
        payload,
        ["newsletterTitleLine1", "newsletterTitle", "titleLine1"],
        FALLBACK.newsletterTitleLine1
      ),
      newsletterTitleLine2: pickString(
        payload,
        ["newsletterTitleLine2", "newsletterTitleAccent", "titleLine2"],
        FALLBACK.newsletterTitleLine2
      ),
      newsletterPlaceholder: pickString(
        payload,
        ["newsletterPlaceholder", "emailPlaceholder"],
        FALLBACK.newsletterPlaceholder
      ),
      newsletterButtonText: pickString(
        payload,
        ["newsletterButtonText", "newsletterButton", "subscribeButton", "buttonText"],
        FALLBACK.newsletterButtonText
      ),
      companyLines: [companyLine1, companyLine2].filter(Boolean),
      locationText: pickString(
        payload,
        ["locationText", "location", "address"],
        FALLBACK.locationText
      ),
      emailText: pickString(
        payload,
        ["emailText", "email", "contactEmail"],
        FALLBACK.emailText
      ),
      websiteText: pickString(
        payload,
        ["websiteText", "website", "site"],
        FALLBACK.websiteText
      ),
      facebookUrl: pickString(payload, ["facebookUrl"], FALLBACK.facebookUrl),
      instagramUrl: pickString(payload, ["instagramUrl"], FALLBACK.instagramUrl),
      linkedinUrl: pickString(payload, ["linkedinUrl"], FALLBACK.linkedinUrl),
      socialIconFacebook: resolveMediaUrl(payload.socialIconFacebook) || fb,
      socialIconInstagram: resolveMediaUrl(payload.socialIconInstagram) || ig,
      socialIconLinkedIn: resolveMediaUrl(payload.socialIconLinkedIn) || li,
      rightLogoImage: resolveMediaUrl(payload.rightLogoImage) || orangeLogo,
      copyrightText: copyrightRaw.replace(/\{year\}/gi, String(year)),
      privacyPolicyText: pickString(
        payload,
        ["privacyPolicyText", "privacyLabel"],
        FALLBACK.privacyPolicyText
      ),
      privacyPolicyUrl: pickString(
        payload,
        ["privacyPolicyUrl"],
        FALLBACK.privacyPolicyUrl
      ),
      termsText: pickString(payload, ["termsText", "termsLabel"], FALLBACK.termsText),
      termsUrl: pickString(payload, ["termsUrl"], FALLBACK.termsUrl),
      sitemapText: pickString(
        payload,
        ["sitemapText", "sitemapLabel"],
        FALLBACK.sitemapText
      ),
      sitemapUrl: pickString(payload, ["sitemapUrl"], FALLBACK.sitemapUrl),
      vucaUrl: pickString(payload, ["vucaUrl"], FALLBACK.vucaUrl),
      vucaLogoImage: resolveMediaUrl(payload.vucaLogoImage) || logo,
    };
  }, [footerContent]);

  return (
    <footer className="footer">
      <div className="footer-top">
        <div className="footer-container footer-newsletter">
          <h2>
            {view.newsletterTitleLine1} <span>{view.newsletterTitleLine2}</span>
          </h2>

          <form
            className="newsletter-input"
            onSubmit={handleSubscribe}
            noValidate
          >
            <input
              type="email"
              name="email"
              autoComplete="email"
              placeholder={view.newsletterPlaceholder}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (feedback.message) setFeedback({ type: "", message: "" });
              }}
              disabled={submitting}
              aria-label={view.newsletterPlaceholder}
            />
            <button type="submit" disabled={submitting}>
              {submitting ? "Subscribing..." : view.newsletterButtonText}
            </button>
          </form>
          {feedback.message ? (
            <p
              className={`newsletter-feedback newsletter-feedback--${feedback.type}`}
              role={feedback.type === "error" ? "alert" : "status"}
            >
              {feedback.message}
            </p>
          ) : null}
        </div>
      </div>

      <div className="footer-container footer-main">
        <div className="footer-left">
          <p>
            {view.companyLines.map((line, idx) => (
              <React.Fragment key={`${line}-${idx}`}>
                {line}
                {idx < view.companyLines.length - 1 ? <br /> : null}
              </React.Fragment>
            ))}
          </p>

          <div className="line" />

          <span>{view.locationText}</span>
          <span>{view.emailText}</span>
          <span>{view.websiteText}</span>

          <div className="socials">
            {view.facebookUrl ? (
              <a href={view.facebookUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={view.socialIconFacebook}
                  alt="Facebook"
                  onError={(e) => {
                    if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
                  }}
                />
              </a>
            ) : null}
            {view.instagramUrl ? (
              <a href={view.instagramUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={view.socialIconInstagram}
                  alt="Instagram"
                  onError={(e) => {
                    if (e.currentTarget.src !== ig) e.currentTarget.src = ig;
                  }}
                />
              </a>
            ) : null}
            {view.linkedinUrl ? (
              <a href={view.linkedinUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={view.socialIconLinkedIn}
                  alt="LinkedIn"
                  onError={(e) => {
                    if (e.currentTarget.src !== li) e.currentTarget.src = li;
                  }}
                />
              </a>
            ) : null}
          </div>
        </div>

        <div className="footer-links">
          <div>
            <h4>{t("footer.quickLinksTitle")}</h4>
            <ul>
              {QUICK_LINKS.map((link) => (
                <li key={`quick-${link.url}-${link.labelKey}`}>
                  <FooterNavLink to={link.url} className="footer-link">
                    {t(link.labelKey)}
                  </FooterNavLink>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4>{t("footer.companyLinksTitle")}</h4>
            <ul>
              {COMPANY_LINKS.map((link) => (
                <li key={`company-${link.url}-${link.labelKey}`}>
                  <FooterNavLink to={link.url} className="footer-link">
                    {t(link.labelKey)}
                  </FooterNavLink>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="footer-right">
          <img
            src={view.rightLogoImage}
            alt="big logo"
            onError={(e) => {
              if (e.currentTarget.src !== orangeLogo) e.currentTarget.src = orangeLogo;
            }}
          />
        </div>
      </div>

      <div className="footer-bottom">
        <div className="footer-container footer-bottom-content">
          <span>{view.copyrightText}</span>

          <div className="footer-bottom-links">
            <FooterNavLink to={view.privacyPolicyUrl} className="footer-link">
              {view.privacyPolicyText}
            </FooterNavLink>
            <FooterNavLink to={view.termsUrl} className="footer-link">
              {view.termsText}
            </FooterNavLink>
            <FooterNavLink to={view.sitemapUrl} className="footer-link">
              {view.sitemapText}
            </FooterNavLink>
            <a href={view.vucaUrl} target="_blank" rel="noopener noreferrer">
              <img
                src={view.vucaLogoImage}
                alt="logo"
                className="footer-logo"
                onError={(e) => {
                  if (e.currentTarget.src !== logo) e.currentTarget.src = logo;
                }}
              />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
